import {
  bindMiniAppCssVars,
  bindThemeParamsCssVars,
  bindViewportCssVars,
  disableVerticalSwipes,
  expandViewport,
  init as initTelegramSdk,
  initDataRaw,
  isTMA,
  miniAppReady,
  mountBackButton,
  mountMiniAppSync,
  mountSwipeBehavior,
  mountThemeParamsSync,
  mountViewport,
  restoreInitData,
  setDebug,
  setMiniAppBackgroundColor,
  setMiniAppHeaderColor,
} from '@telegram-apps/sdk-react'

/** Matches the `canvas` color in src/styles/index.css so the Telegram chrome blends with the app. */
const APP_BACKGROUND_COLOR = '#0C0F14'

export class TelegramEnvironmentError extends Error {
  constructor() {
    super('The app is not running inside Telegram Mini Apps')
    this.name = 'TelegramEnvironmentError'
  }
}

interface InitTelegramOptions {
  isDebugEnabled: boolean
}

/**
 * Initialises the Telegram SDK and mounts every component the app relies on.
 * Each step checks availability because older Telegram clients do not support all methods.
 */
export async function initTelegram({ isDebugEnabled }: InitTelegramOptions): Promise<void> {
  if (!isTMA()) {
    throw new TelegramEnvironmentError()
  }

  setDebug(isDebugEnabled)
  initTelegramSdk()
  restoreInitData()

  mountBackButton.ifAvailable()

  if (mountThemeParamsSync.isAvailable()) {
    mountThemeParamsSync()
    bindThemeParamsCssVars.ifAvailable()
  }

  if (mountMiniAppSync.isAvailable()) {
    mountMiniAppSync()
    bindMiniAppCssVars.ifAvailable()
    setMiniAppHeaderColor.ifAvailable(APP_BACKGROUND_COLOR)
    setMiniAppBackgroundColor.ifAvailable(APP_BACKGROUND_COLOR)
  }

  if (mountViewport.isAvailable()) {
    await mountViewport()
    bindViewportCssVars.ifAvailable()
    expandViewport.ifAvailable()
  }

  // Vertical swipes close the mini app; disabling them keeps scrolling inside pages safe.
  if (mountSwipeBehavior.isAvailable()) {
    mountSwipeBehavior()
    disableVerticalSwipes.ifAvailable()
  }

  miniAppReady.ifAvailable()
}

/**
 * Raw init data string signed by Telegram. The backend must validate it with the bot token
 * before trusting any user field; the frontend only forwards it.
 */
export function getTelegramInitDataRaw(): string | undefined {
  return initDataRaw()
}
